import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import React, { FC, useContext } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import { AddTagIcon } from '../../../../vectors/index';
import { useCreatetags } from '@features/applications/hooks/useCreatetags';
import { useUpdatetags } from '@features/applications/hooks/useUpdatetags';
import { toast } from 'react-toastify';
import { useEmployerCreatetags } from '@features/applications/hooks/useEmployerCreatetags';
import { useUpdateEmployertags } from '@features/applications/hooks/useUpdateEmployertags';
import { authContext } from '@staffedupusa/shared-components';
import { getStrippedText } from '@utils/helper';

type Tag = {
  id: number;
  tag: string;
} | null;

type AddNewTagModalProps = {
  handleClose: () => void;
  editable: Tag;
  selectedCompanyId?: number | string;
  templateId?: number;
};

const AddNewTagModal: FC<AddNewTagModalProps> = ({
  handleClose,
  selectedCompanyId,
  templateId,
  editable,
}) => {
  const { authData } = useContext(authContext);
  const [errorText, setErrorText] = React.useState<string>('');
  const [newTag, setNewTag] = React.useState<string>(
    editable ? editable.tag : '',
  );

  const { createTags, isCreateTagsLoading } = useCreatetags({ setErrorText });
  const { updatetags, isupdatetagsLoading } = useUpdatetags({ setErrorText });

  const { createEmployerTags, isCreateEmployerTagsLoading } =
    useEmployerCreatetags({ setErrorText });
  const { updateEmployertags, isupdateEmployertagsLoading } =
    useUpdateEmployertags({ setErrorText });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data.status == 200) {
          toast.success(data.message);
          handleClose();
        }
      },
    };

    if (!editable) {
      if (authData.user?.enterprise) {
        createTags({ tag: newTag, company_id: selectedCompanyId, template_id: templateId }, options);
      } else {
        createEmployerTags({ tag: newTag }, options);
      }
    } else if (editable) {
      if (authData.user?.enterprise) {
        updatetags(
          { tag_id: editable.id, tag: newTag, company_id: selectedCompanyId, template_id: templateId },
          options,
        );
      } else {
        updateEmployertags({ tag_id: editable.id, tag: newTag }, options);
      }
    }
  };

  return (
    <>
      <Dialog
        open
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <br />
          <AddTagIcon />
          <br />
          <Typography variant="h4" sx={{ p: 3 }}>
            {editable ? (
              <span>
                Update <b>{editable.tag}</b>
              </span>
            ) : (
              'Add New Tag '
            )}
          </Typography>
        </DialogTitle>

        <DialogContent>
          {errorText && (
            <Alert sx={{marginBottom:'10px'}} severity="error">
              {errorText}
            </Alert>
          )}
          <form style={{ marginBottom: 5 }} onSubmit={handleSubmit}>
            <TextField
              variant="outlined"
              size="small"
              color="secondary"
              placeholder="Tag..."
              fullWidth
              sx={{ maxWidth: '100%' }}
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
            />
            <Button
              sx={{ mt: 4, width: '100%' }}
              startIcon={<DoneIcon />}
              disabled={
                !newTag ||
                getStrippedText(newTag).length === 0 ||
                isCreateTagsLoading ||
                isCreateEmployerTagsLoading ||
                isupdatetagsLoading ||
                isupdateEmployertagsLoading
              }
              variant="contained"
              type="submit"
              color="primary"
            >
              {(!isCreateTagsLoading || !isCreateEmployerTagsLoading) &&
              (!isupdatetagsLoading || !isupdateEmployertagsLoading) &&
              editable
                ? 'Update'
                : ' Add New Tag'}
              {(isCreateTagsLoading ||
                isCreateEmployerTagsLoading ||
                isupdatetagsLoading ||
                isupdateEmployertagsLoading) && <span> &nbsp;Loading...</span>}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddNewTagModal;
